import React, { useState, useCallback } from 'react'
import { Control, UseFormSetValue, FieldValues } from 'react-hook-form'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Stack, Typography, Grid, Divider } from '@mui/material'
import { ExtraFieldsProvider } from 'templates/Settings/Body/Sections/ExtraFields/Extrafields.context'
import ExtraFieldsAutocomplete from 'templates/v2/RegistrationLink/components/Info/ExtraInfo/components/ExtraFieldsConfiguration/components/ExtraFieldsAutocomplete'
import { ExtraFieldsType } from 'types/CustomFields'

import CustomFieldRenderer from 'components/CustomFieldRenderer'

import { UploadSettings } from 'utils/customFieldUpload'

import { theme } from 'styles/theme'

export interface ExtraFieldItem {
  uuid: string
  name: string
  required: boolean
  extra_fieldable_type?: string | number
  field_type?: string | number
  options?: Array<{ label: string; value: string }>
  // SC-11348: o renderer do upload precisa da configuração (tipos aceitos,
  // vários arquivos e limites)
  field_configuration?: Record<string, any>
  upload_settings?: UploadSettings
  // SC-11487: seção, condição e cópia de dados no formulário do Link de Cadastro
  display_settings?: {
    section?: string
    visible_when?: { custom_field_uuid: string; values: string[] }
    copy_from?: string
  }
}

interface ExtraFieldsManagerProps<T extends FieldValues> {
  control: Control<T>
  setValue: UseFormSetValue<T>
  items: ExtraFieldItem[]
  onItemsChange: (items: ExtraFieldItem[]) => void
  showAddButton?: boolean
  title?: string
  addButtonText?: string
  renderFieldWhenSelected?: boolean
  excludedValues?: string[]
  hasDefaultCustomFields?: boolean
  excludeFieldTypes?: string[]
  setUploadIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
  uploadIsLoading?: boolean
  setUploadIsDirty?: React.Dispatch<React.SetStateAction<boolean>>
  onFieldCreationStart?: () => void
  onFieldCreationEnd?: () => void
}

const ExtraFieldsManager = <T extends FieldValues>({
  control,
  setValue,
  items,
  onItemsChange,
  uploadIsLoading,
  setUploadIsLoading,
  setUploadIsDirty,
  showAddButton = true,
  title,
  hasDefaultCustomFields,
  addButtonText = 'Novo campo',
  renderFieldWhenSelected = false,
  excludedValues = [],
  excludeFieldTypes = [],
  onFieldCreationStart,
  onFieldCreationEnd
}: ExtraFieldsManagerProps<T>) => {
  const [selectedExtraField, setSelectedExtraField] = useState<any>(null)
  const [extraFieldsList, setExtraFieldsList] = useState<ExtraFieldsType>()
  const [showSelectExtraField, setShowSelectExtraField] =
    useState<boolean>(false)

  const mapFieldType = (fieldType: string | number | undefined): string => {
    if (typeof fieldType === 'number') {
      switch (fieldType) {
        case 0:
          return 'short_text'
        case 1:
          return 'radio'
        case 2:
          return 'checkbox'
        case 3:
          return 'accept'
        case 4:
          return 'upload'
        case 5:
          return 'long_text'
        case 6:
          return 'date'
        default:
          return 'short_text'
      }
    }

    if (typeof fieldType === 'string') {
      switch (fieldType) {
        case 'text':
          return 'short_text'
        case 'check_box':
          return 'checkbox'
        case 'accept':
          return 'accept'
        default:
          return fieldType
      }
    }

    return 'short_text'
  }

  const handleAddExtraField = useCallback(
    (_event: any, newValue: any) => {
      if (!newValue) return
      const fullFieldData = extraFieldsList?.find(
        (field) => field.uuid === newValue.value
      )

      const newField: ExtraFieldItem = {
        name: newValue.label,
        uuid: newValue.value,
        required: newValue.required || false,
        extra_fieldable_type: newValue.type || fullFieldData?.field_type,
        field_type: newValue.type || fullFieldData?.field_type,
        options: (fullFieldData as any)?.field_configuration?.options || [],
        field_configuration:
          fullFieldData?.field_configuration || newValue.field_configuration,
        upload_settings:
          fullFieldData?.upload_settings || newValue.upload_settings
      }
      const updatedItems = [...items, newField]
      onItemsChange(updatedItems)

      setSelectedExtraField(null)
      setShowSelectExtraField(false)
    },
    [items, onItemsChange, extraFieldsList]
  )

  const handleRemoveField = useCallback(
    (fieldUuid: string) => {
      const newItems = items.filter((item) => item.uuid !== fieldUuid)
      onItemsChange(newItems)
    },
    [items, onItemsChange]
  )

  return (
    <Grid container>
      <Box>
        <Typography variant="smBold">{title}</Typography>
      </Box>
      {renderFieldWhenSelected && items.length > 0 && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {items.map((field) => (
              <Grid item xs={12} key={field.uuid}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ position: 'relative', flexGrow: 1 }}>
                    <CustomFieldRenderer
                      setUploadIsDirty={setUploadIsDirty}
                      setUploadIsLoading={setUploadIsLoading}
                      isLoading={uploadIsLoading}
                      field={{
                        uuid: field.uuid,
                        name: field.name,
                        required: field.required,
                        field_type: mapFieldType(
                          field.extra_fieldable_type || field.field_type
                        ),
                        options: field.options,
                        field_configuration: field.field_configuration,
                        upload_settings: field.upload_settings
                      }}
                      control={control}
                      fieldNamePrefix="extraField"
                      setValue={setValue}
                    />
                  </Box>
                  <Icon
                    icon="close"
                    onClick={() => handleRemoveField(field.uuid)}
                    sx={{
                      mt: '24px',
                      ml: '8px',
                      cursor: 'pointer',
                      color: theme.palette.neutral[600],
                      fontSize: '16px',
                      '&:hover': {
                        color: theme.palette.error[300]
                      }
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {(showSelectExtraField || !hasDefaultCustomFields) && (
        <Grid item xs={12} mt="16px">
          <ExtraFieldsProvider shouldRun={false}>
            <ExtraFieldsAutocomplete
              value={selectedExtraField}
              setExtraFieldsList={setExtraFieldsList}
              onChange={handleAddExtraField}
              setValue={setValue}
              excludedValues={[
                ...items.map((item) => item.uuid),
                ...excludedValues
              ]}
              excludeFieldTypes={excludeFieldTypes}
              onFieldCreationStart={onFieldCreationStart}
              onFieldCreationEnd={onFieldCreationEnd}
            />
          </ExtraFieldsProvider>
        </Grid>
      )}
      {showAddButton && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Stack
            onClick={() => {
              setSelectedExtraField(null)
              setShowSelectExtraField(true)
            }}
            sx={{ cursor: 'pointer' }}
            direction="row"
            gap="5px"
            alignItems="center"
          >
            <Icon
              icon="add"
              sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
            />
            <Typography
              sx={{
                color: theme.palette.neutral[400],
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {addButtonText}
            </Typography>
          </Stack>
        </Grid>
      )}
    </Grid>
  )
}

export default ExtraFieldsManager
